import { useState } from "react";
import { Modal, Button, Form } from "react-bootstrap";
import toast from "react-hot-toast";
import { guardarFechaLoteCapex } from "../../utils/guardarFechaLoteCapex";
import { MODOS_VALIDACION } from "../../utils/validarFechaDocumento";
import SelectorFechaDocumento from "../SelectorFechaDocumento";

// Editor de fecha de pago para las líneas de LOTE del tab CapEx (FOB + componentes), gemelo del
// ModificarFechaProyeccionCapexModal de los activos pero con destino y reglas propias del lote:
//   · Línea FOB (sin costo_id)  → actualizarLote(loteId, { fecha_planificada }).
//   · Línea componente (costo_id) → actualizarCostoLote(costoId, { fecha_pago }).
// A diferencia del editor de activo, NO hay piso HOY: la fecha del lote/componente admite vencidas
// (10.159). La validación de orden (FOB ≤ Transporte < Aduana) y la escritura viven en el util
// standalone src/utils/guardarFechaLoteCapex.js (Fase 1 de "Futuros: reagendar", 2026-09-23) — este
// modal y el despachador `reagendarEntryFuturo` del panel de Futuros llaman a LA MISMA función (10.1).
//
// B.3 Commit 6 (2026-09-26, camino #5): el writer (guardarFechaLoteCapex) ya validaba modo
// DATO_DOCUMENTO fijo desde B.2b — sin cambios ahí. Este commit solo migra el WIDGET al calendario
// compartido (SelectorFechaDocumento, modo DATO_DOCUMENTO — no deshabilita ningún día, mismo
// comportamiento visual que el `<input type="date">` sin `min` de antes), por la regla de la fase
// (cada modal migrado usa el mismo componente, 10.1).

const normalizarFecha = (fechaStr) => {
  if (!fechaStr) return "";
  return String(fechaStr).includes("T") ? String(fechaStr).substring(0, 10) : String(fechaStr);
};

const formatMonto = (entry) => {
  const n = Math.abs(Number(entry?.importeEnMoneda || 0));
  const num = n.toLocaleString("es-CL", { maximumFractionDigits: 0 });
  const moneda = entry?.moneda_origen === "USD" ? "US$ " : "";
  return `${moneda}${num}`;
};

const ModificarFechaLoteCapexModal = ({ show, onHide, entry, onSaved }) => {
  const [fecha, setFecha] = useState(normalizarFecha(entry?.fecha_display));
  const [guardando, setGuardando] = useState(false);

  if (!entry) return null;

  // FOB vs componente: la línea de componente lleva costo_id (id real en lote_costos); la del FOB no.
  const esComponente = !!entry.costo_id;
  const tipoLabel = esComponente ? entry.folio || "Componente" : "FOB";

  const handleGuardar = async () => {
    if (!fecha) {
      toast.error("La fecha es obligatoria");
      return;
    }
    setGuardando(true);
    try {
      const resultado = await guardarFechaLoteCapex({ entry, nuevaFecha: fecha });
      if (resultado.error) {
        toast.error(resultado.error);
        return; // no escribe ni FOB ni componente
      }
      toast.success("Fecha de pago actualizada");
      // Patch puntual (Fase 2A): el padre actualiza `componentesLotes` (por costo_id) o `capexLotes`
      // (por lote_id) según el entry, sin refetch.
      onSaved?.(fecha);
    } catch (e) {
      toast.error(`Error al guardar: ${e.message || e}`);
    } finally {
      setGuardando(false);
    }
  };

  return (
    <Modal show={show} onHide={onHide} centered>
      <Modal.Header closeButton>
        <Modal.Title style={{ fontSize: "1.1rem" }}>Modificar fecha de pago</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div style={{ fontSize: "0.9rem", lineHeight: 1.7, marginBottom: 16 }}>
          <div>
            <strong>Concepto:</strong> {entry.razon_social_receptor || "—"}
          </div>
          <div>
            <strong>Tipo:</strong> {tipoLabel}
          </div>
          <div>
            <strong>Monto:</strong> {formatMonto(entry)}
          </div>
        </div>

        <Form.Group>
          <Form.Label style={{ fontWeight: 600, fontSize: "0.9rem" }}>Fecha de pago</Form.Label>
          <SelectorFechaDocumento
            value={fecha}
            onChange={(nuevaFecha) => setFecha(nuevaFecha || "")}
            modo={MODOS_VALIDACION.DATO_DOCUMENTO}
            disabled={guardando}
          />
        </Form.Group>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" size="sm" onClick={onHide} disabled={guardando}>
          Cancelar
        </Button>
        <Button variant="primary" size="sm" onClick={handleGuardar} disabled={guardando}>
          {guardando ? "Guardando…" : "Guardar"}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default ModificarFechaLoteCapexModal;
